import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

// ─── Types de la REQUÊTE ───────────────────────────────────

interface ImageBbox {
  id: string;
  top_left_x: number;
  top_left_y: number;
  bottom_right_x: number;
  bottom_right_y: number;
  image_base64: string;
}

interface OcrPage {
  index: number;
  markdown: string;
  dimensions: number;
  bbox: any[];
  images: ImageBbox[];
  annotations?: { [schemaName: string]: any };
}

interface OcrResponse {
  id: string;
  model: string;
  pages: OcrPage[];
  usage: {
    total_pages: number;
    total_tokens: number;
  };
}

export interface OcrRequestParams {
  pngBlob: Blob;
  apiKey: string;
  schemaName: string;
  schemaJson: string | object;
  prompt?: string;
}

// ─── Service ───────────────────────────────────────────────
// ============================================================================================================================
// JLG - 2026-10-07
// service opérationnel sans l'anti-virus dans le navigateur. 
// l'url de mistral-ai est black-listée par avast 
// De ce fait passage à la version back-end en Java
// ============================================================================================================================
@Injectable({ providedIn: 'root' })
export class WksMistralOcrService {

  private readonly apiUrl = 'https://api.mistral.ai/v1/ocr';

  constructor(private http: HttpClient) {}

  ocrAnnote(params: OcrRequestParams): Observable<any> {
    const schema = typeof params.schemaJson === 'string'
      ? JSON.parse(params.schemaJson)
      : params.schemaJson;

    return from(this.blobToBase64(params.pngBlob)).pipe(
      map(imageBase64 => this.buildBody(params, schema, imageBase64)),
      mergeMap(body => this.callOcr(params.apiKey, params.schemaName, body))
    );
  }

  /** Construction du corps de la requête POST /v1/ocr */
  private buildBody(params: OcrRequestParams, schema: any, imageBase64: string) {
    const body: any = {
      model: 'mistral-ocr-latest',
      document: {
        type: 'image_url',
        image_url: imageBase64          // data:image/png;base64,...
      },
      document_annotation_format: {      // ← et non document_annotation
        type: 'json_schema',
        json_schema: {
          name: params.schemaName,
          strict: true,
          schema: schema
        }
      }
    };
    if (params.prompt && params.prompt.trim()) {
      body.document_annotation_prompt = params.prompt;
    }
    return body;
  }

  /** Appel HTTP + extraction de l'annotation */
  private callOcr(apiKey: string, schemaName: string, body: any): Observable<any> {
    const headers = new HttpHeaders({ Authorization: 'Bearer ' + apiKey });

    return this.http.post<OcrResponse>(this.apiUrl, body, { headers }).pipe(
      // dans callOcr, remplacez le map actuel par :
      map(response => {
        // Annotation au niveau racine, en chaîne JSON
        const raw = (response as any).document_annotation;

        if (raw === undefined || raw === null) {
          throw new Error('Annotation introuvable. Clés racine : '
            + Object.keys(response).join(', '));
        }

        // document_annotation est une chaîne JSON -> parse
        return typeof raw === 'string' ? JSON.parse(raw) : raw;
      })
    );
  }

  /** Blob → data URI base64 */
  private async blobToBase64(blob: Blob): Promise<string> {
    // const bytes = new Uint8Array(await new Response(blob).arrayBuffer());
    const bytes = await new Promise<Uint8Array>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(new Uint8Array(reader.result as ArrayBuffer));
        };
        reader.onerror = () => {
            reject(reader.error);
        };
        reader.readAsArrayBuffer(blob);
    });
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk) as any);
    }
    return 'data:image/png;base64,' + btoa(binary);
  }
}
